import { useMemo, useState } from 'react'

import Link from 'next/link'

import { cx } from '@linaria/core'
import { useErrorBoundary } from 'react-error-boundary'
import { ClipLoader } from 'react-spinners'

import { dinnerUpdateDinner } from '~/api-client/food_api/dinner'
import { Dinner } from '~/api-definition/definition'
import { AppImage } from '~/components/data-display/AppImage'
import { FormatDateTime } from '~/components/data-display/FormatDateTime'
import { SelectV2 } from '~/components/data-input/SelectV2'
import { GuestIcon, MapIcon } from '~/components/icons'
import {
  dinnerAreaAndGenre,
  dinnerMainThumbnailImage,
  dinnerOrganizer,
  dinnerOrganizerDisplayName,
  dinnerParticipationStatusText,
  dinnerRecruitmentProgress,
  dinnerRestaurantName,
} from '~/libs/dinner'
import { useAccessToken } from '~/provider/SessionProvider'

import { UserWarningStatusIconButton } from '../user/UserWarningStatus'

export const DinnerSummary: React.FC<{
  dinner: Dinner
  showParticipationStatus: boolean
  invert?: boolean
  showDate?: boolean
  showPrivacyTypeSelect?: boolean
  hideRecruitmentProgress?: boolean
  hidePrivacyType?: boolean
  omitToday?: boolean
}> = (props) => {
  const accessToken = useAccessToken()
  const { showBoundary } = useErrorBoundary()
  const organizer = dinnerOrganizer(props.dinner)

  const participationStatusText = useMemo(() => {
    return props.showParticipationStatus
      ? dinnerParticipationStatusText(props.dinner)
      : undefined
  }, [props.dinner, props.showParticipationStatus])

  const [isUpdatingPrivacyType, setIsUpdatingPrivacyType] = useState(false)

  const handleChangePrivacyType = async (value: string) => {
    if (accessToken) {
      try {
        setIsUpdatingPrivacyType(true)
        await dinnerUpdateDinner({
          accessToken,
          body: {
            dinner_id: props.dinner.dinnerId,
            privacy_type: value as Dinner['privacyType'],
          },
        })
        await new Promise((resolve) => setTimeout(resolve, 1000))
      } catch (e) {
        showBoundary(e)
      } finally {
        setIsUpdatingPrivacyType(false)
      }
    }
  }

  return (
    <div>
      <Link
        href={`/dinners/${props.dinner.dinnerId}`}
        className={cx(
          'flex gap-4',
          props.invert && 'py-5 px-4 bg-primary-cv2 rounded'
        )}
      >
        <div>
          <AppImage
            variant={{ type: 'cover', coverPosition: 'center' }}
            rounded="md"
            width={75}
            height={75}
            alt={`投稿画像`}
            src={dinnerMainThumbnailImage(props.dinner)}
          />
          {!props.hidePrivacyType && props.dinner.privacyType === 'public' && (
            <div className="text-xs bg-function-cv1 text-white px-2 py-1 rounded-full mt-2 text-center">
              全体公開
            </div>
          )}
          {!props.hidePrivacyType && props.dinner.privacyType === 'private' && (
            <div className="text-xs bg-function-cv2 px-2 py-1 rounded-full mt-2 text-center">
              限定公開
            </div>
          )}
          {!props.hidePrivacyType &&
            props.dinner.privacyType === 'following' && (
              <div className="text-xs bg-function-cv2 px-2 py-1 rounded-full mt-2 text-center">
                グループ公開
              </div>
            )}
        </div>
        <div className="flex-1">
          {props.showDate == null ? (
            <div className="flex items-center">
              <div className="font-bold my-1">
                {dinnerRestaurantName(props.dinner)}
              </div>
              <div className="ml-auto text-xs text-black flex items-center gap-1">
                {dinnerOrganizerDisplayName(props.dinner)}
                {organizer?.user &&
                  (organizer.user as any).warningStatus != null && (
                    <UserWarningStatusIconButton
                      userWarningStatus={(organizer.user as any).warningStatus}
                    />
                  )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-x-2">
                <div className="font-bold text-sm">
                  <FormatDateTime
                    variant="ja"
                    date={new Date(props.dinner.scheduledAt * 1000)}
                    omitCurrentYear
                    omitToday
                  />
                </div>
                <div className="ml-auto text-xs text-black flex items-center gap-1">
                  {dinnerOrganizerDisplayName(props.dinner)}
                  {organizer?.user &&
                    (organizer.user as any).warningStatus != null && (
                      <UserWarningStatusIconButton
                        userWarningStatus={
                          (organizer.user as any).warningStatus
                        }
                      />
                    )}
                </div>
              </div>
              <div className="font-bold my-1">
                {dinnerRestaurantName(props.dinner)}
              </div>
            </>
          )}
          <div className="text-xs opacity-50 flex items-center gap-1 break-all">
            {dinnerAreaAndGenre(props.dinner).length > 0 ? (
              <MapIcon className="fill-[#999]" />
            ) : (
              <></>
            )}
            {dinnerAreaAndGenre(props.dinner)}
          </div>
          <div className="text-xs mt-1 break-all">
            {props.dinner.dinnerName}
          </div>
          {!props.hideRecruitmentProgress &&
            (participationStatusText ? (
              <>
                <div className="flex mt-2 w-full">
                  <div
                    className={cx(
                      'grow rounded-l text-xs flex items-center gap-1 px-2 py-1',
                      props.invert
                        ? 'bg-white'
                        : props.dinner.seatNumber === 0
                        ? 'bg-[#E6E6EA]'
                        : 'bg-primary-cv2'
                    )}
                  >
                    <GuestIcon className="fill-text" />
                    {dinnerRecruitmentProgress(props.dinner)}
                  </div>
                  <div className="text-xs text-white bg-[#C69E51] px-2 py-1 rounded-r">
                    {participationStatusText}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex mt-2 w-full">
                  <div
                    className={cx(
                      'grow rounded text-xs flex items-center gap-1 px-2 py-1',
                      props.invert
                        ? 'bg-white'
                        : props.dinner.seatNumber === 0
                        ? 'bg-[#E6E6EA]'
                        : 'bg-primary-cv2'
                    )}
                  >
                    <GuestIcon className="fill-text" />
                    {dinnerRecruitmentProgress(props.dinner)}
                  </div>
                </div>
              </>
            ))}
        </div>
      </Link>
      {props.showPrivacyTypeSelect && (
        // ml-95px は、上のコンポーネントの gap-4 と w-[75px] に合わせている
        <div className="mt-2 ml-[91px]" onClick={(e) => e.stopPropagation()}>
          <SelectV2
            label="表示範囲"
            name="privacyType"
            fullWidth
            required
            options={[
              { label: '全体公開', value: 'public' },
              { label: '非公開', value: 'private' },
              // TODO: api 実装後表示する
              // { label: 'フォロワーのみ公開', value: 'following' },
            ]}
            onChange={handleChangePrivacyType}
            disabled={isUpdatingPrivacyType}
            defaultValue={props.dinner.privacyType}
            rightIcon={
              isUpdatingPrivacyType ? (
                <ClipLoader loading={true} className="w-5 h-5" color="#666" />
              ) : undefined
            }
          />
        </div>
      )}
    </div>
  )
}
